<template>
  <!-- Plan 63: families who answered the goodbye email, said we can contact them, and left a low
       rating / "Not the right fit" / a comment. Logging a call, text or email with them (the same
       contact_interactions row as anywhere else) or "No call needed" closes the card.
       Signups staff only; teachers never see these. Nothing renders when the list is empty. -->
  <div v-if="rows.length" class="space-y-2 mb-6">
    <h3 class="text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-widest mb-2">
      Departures to call ({{ rows.length }})
    </h3>
    <div v-for="r in rows" :key="r.id" class="card border-rose-300 dark:border-rose-700 p-3 text-sm">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <button class="font-semibold text-fg-base dark:text-fg-base-dark hover:underline text-left" @click="openContact(r.contact_id)">{{ r.contact }}</button>
          <span class="text-fg-muted dark:text-fg-muted-dark"> · {{ r.students.join(', ') }} with {{ r.teachers.join(', ') || '—' }}</span>
          <a v-if="r.phone" :href="`tel:${r.phone}`" class="block text-xs text-blue-600 dark:text-blue-400">{{ formatPhone(r.phone) }}</a>
        </div>
        <LogTouchButtons :disabled="busy" @log="startLog(r, $event)" />
      </div>
      <p class="mt-1.5 text-fg-secondary dark:text-fg-secondary-dark">
        <span v-if="r.reason_label">{{ r.reason_label }}</span>
        <span v-if="r.rating"> · rated lessons {{ r.rating }}/5</span>
        <span class="text-fg-muted dark:text-fg-muted-dark"> · last lesson {{ fmtDate(r.last_lesson_date) }}</span>
      </p>
      <p v-if="r.comment" class="mt-1 text-fg-base dark:text-fg-base-dark">“{{ r.comment }}”</p>

      <div v-if="logging?.id === r.id" class="mt-2 flex gap-2">
        <input v-model="note" type="text" :placeholder="logging.kind === 'note' ? 'Note' : 'Note (optional)'" :class="inputClass" @keyup.enter="saveLog" />
        <button :class="primary" :disabled="busy || (logging.kind === 'note' && !note.trim())" @click="saveLog">Save</button>
        <button :class="btn" @click="logging = null">Cancel</button>
      </div>
      <div v-else class="mt-2">
        <button :class="btn" :disabled="busy" @click="noCall(r)">No call needed</button>
      </div>
    </div>
    <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useApi } from '../../composables/useApi.js'
import { useAdminSidebar } from '../../composables/useAdminSidebar.js'
import { formatPhone } from '../../composables/useSignupFormat.js'
import LogTouchButtons from './LogTouchButtons.vue'

const { fetchApi, logInteraction } = useApi()
const sidebar = useAdminSidebar()
const router = useRouter()

const rows = ref([])
const logging = ref(null)   // { id, contactId, kind, outcome }
const note = ref('')
const busy = ref(false)
const error = ref('')

async function load() {
  try {
    rows.value = (await fetchApi('/api/departure-feedback/to-call')).departures
  } catch { /* not shown when it can't load */ }
}
load()

function openContact(contactId) {
  if (sidebar.available.value) sidebar.open('contact', { contactId })
  else router.push(`/admin/contacts/directory?contact=${contactId}`)
}

function startLog(r, { kind, outcome }) {
  logging.value = { id: r.id, contactId: r.contact_id, kind, outcome }
  note.value = ''
  error.value = ''
}

async function run(fn) {
  busy.value = true
  error.value = ''
  try {
    await fn()
    await load()
  } catch (e) {
    error.value = e.message || 'Something went wrong.'
  } finally {
    busy.value = false
  }
}

// The backend closes the card on a logged call / text / email (departure_feedback.note_touch); a note doesn't.
const saveLog = () => run(async () => {
  const { contactId, kind, outcome } = logging.value
  await logInteraction(contactId, { kind, outcome, note: note.value.trim() || null })
  logging.value = null
})

const noCall = (r) => run(() => fetchApi(`/api/departure-feedback/${r.id}/no-call`, { method: 'POST', body: JSON.stringify({}) }))

const fmtDate = (iso) => iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  : ''

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark disabled:opacity-50'
const primary = 'px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white disabled:opacity-50'
const inputClass = 'flex-1 min-w-0 px-2 py-1 text-sm rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-2 focus:ring-blue-500'
</script>
